import { compact, take } from "lodash";
import pluralize from "pluralize";
import { useMemo, useState } from "react";
import { CitationFragmentFragment } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import AskAICitationModal from "@apps/ask-ai/components/citations-modal";
import { currentOrganizationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import MotionDiv from "@components/motion/motion-div";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

const MAX_CHUNK_SIZE = 10;

const AssessmentQuestionStackedTextAnswers = ({
  answers,
  viewMoreEnabled,
  showContextSidebarCTA,
  isSurvey = false,
}: {
  questionId: number;
  answers: {
    responder?: BasicUser | null;
    text?: string | null;
    answerValue?: string | null;
    answerId?: number | null;
    inferredSources?: {
      edges: ReadonlyArray<{
        node?: CitationFragmentFragment | null;
      } | null>;
    } | null;
  }[];
  showContextSidebarCTA?: boolean;
  viewMoreEnabled?: boolean;
  isSurvey?: boolean;
}) => {
  const organization = currentOrganizationVar();
  const [answerLimit, setAnswerLimit] = useState(MAX_CHUNK_SIZE);
  const [showCitationsModalAnswerId, setShowCitationsModalAnswerId] = useState<
    number | null
  >(null);

  const answerData = useMemo(() => {
    const data = viewMoreEnabled ? take(answers, answerLimit) : answers;
    return data.filter((answer) => answer.text);
  }, [answers, answerLimit, viewMoreEnabled]);

  const citations = useMemo(() => {
    if (!showCitationsModalAnswerId) return [];
    const matchingAnswer = answerData.find(
      (answer) => answer.answerId === showCitationsModalAnswerId,
    );
    if (!matchingAnswer) return [];
    return compact(
      matchingAnswer.inferredSources?.edges?.map((edge) => edge?.node) || [],
    );
  }, [answerData, showCitationsModalAnswerId]);

  return (
    <div className="flex flex-col gap-6">
      {showCitationsModalAnswerId && (
        <AskAICitationModal
          citations={citations}
          onClose={() => {
            setShowCitationsModalAnswerId(null);
          }}
        />
      )}
      {answerData.map((answer, index) => {
        const sourceCount = answer.inferredSources?.edges?.length || 0;
        const responderId = answer.responder?.id;
        return (
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            key={index}
            className="text-sm"
          >
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <Avatar className="mr-2" user={answer.responder} size="5" />
                <div className="text-ellipsis font-medium text-gray-600 overflow-hidden text-nowrap">
                  {answer.responder ? answer.responder.name : "Anonymous"}
                </div>
              </div>
              {answer.answerValue && (
                <div className="text-xs text-gray-500">
                  (Answer: {answer.answerValue})
                </div>
              )}
            </div>
            <div className="mt-2">
              <TextareaWysiwyg
                className="max-w-full"
                editable={false}
                value={answer.text}
              />
            </div>
            {!isSurvey &&
              showContextSidebarCTA &&
              organization.featureFlags.reviewContextGeneration &&
              responderId && (
                <div className="mt-2 hidden lg:flex">
                  <Button
                    onClick={() => {
                      setShowCitationsModalAnswerId(answer.answerId || null);
                    }}
                    size={ButtonSize.medium}
                    theme={buttonTheme.lightBlue}
                    disabled={sourceCount === 0}
                    text={`${sourceCount} ${pluralize("Source", sourceCount)}${sourceCount > 0 ? " detected" : ""}`}
                  />
                </div>
              )}
          </MotionDiv>
        );
      })}
      {viewMoreEnabled && answerLimit < answers.length && (
        <div className="mt-6">
          <Button
            theme={buttonTheme.lightGray}
            onClick={() => setAnswerLimit(answerLimit + MAX_CHUNK_SIZE)}
            text="View More"
          />
        </div>
      )}
    </div>
  );
};

export default AssessmentQuestionStackedTextAnswers;
